import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRunStore } from '../../state/runStore';
import { useLookAtThatStore } from '../../state/lookAtThatStore';
import { LookAtThatViewer } from './GameViewer';
import { LookAtThatControls } from './ControlPanel';
import { LookAtThatLog } from './ActionLog';
import { LookAtThatChat } from './ChatPanel';
import { CalibrationCard } from './CalibrationCard';
import { TestQueuePanel } from './TestQueuePanel';
import { GameMemoryCard } from './GameMemoryCard';
import { ExploreGallery } from './ExploreGallery';

export function LookAtThatPage() {
  const { id } = useParams<{ id: string }>();
  const loadEnvLinks = useRunStore((s) => s.loadEnvLinks);
  const envLinks = useRunStore((s) => s.envLinks);
  const tests = useRunStore((s) => s.tests);
  const loadRun = useRunStore((s) => s.loadRun);
  const currentRun = useRunStore((s) => s.current);
  const store = useLookAtThatStore();

  useEffect(() => {
    if (!id) return;
    void store.load(id);
    void loadEnvLinks(id);
    // The play queue reuses the Phase 5 test rows; make sure they are loaded.
    if (!currentRun || currentRun.id !== id || tests.length === 0) void loadRun(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const engineOk = store.status?.engine.ok ?? false;

  return (
    <div className="flex-col look-at-that-page" style={{ gap: 12 }}>
      <div>
        <h1 style={{ margin: '0 0 4px' }}>Look at that</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Live Playwright session. Calibrate to teach the bot the UI, chat to ask questions, queue
          TEXEC in-game tests, or explore free-form. Login pages pause for credentials (studio recipe
          first).
        </p>
      </div>

      <div className="look-at-that-grid">
        <LookAtThatViewer
          screenshot={store.screenshot}
          width={store.width}
          height={store.height}
          status={
            store.awaitingCredentials
              ? 'Waiting for credentials'
              : store.calibrating
                ? store.stuckStep
                  ? `Calibrating — stuck on ${store.stuckStep}`
                  : 'Calibrating'
                : store.playingKey
                  ? `Playing ${store.playingKey}`
                  : store.executing
                    ? 'Working'
                    : store.running
                      ? 'Session open'
                      : 'Idle'
          }
          lastAction={store.lastAction}
          analyzing={store.executing}
          cursor={store.cursor}
          typing={store.typing}
          adapter={store.adapter}
        />
        <LookAtThatControls
          envLinkRows={envLinks?.jurisdictions ?? null}
          recipeUser={store.recipeUser ?? store.status?.recipeUser ?? null}
          hasRecipePassword={store.status?.hasRecipePassword ?? false}
          engineOk={engineOk}
          engineError={store.status?.engine.error}
          running={store.running}
          executing={store.executing}
          awaitingCredentials={store.awaitingCredentials}
          error={store.error}
          adapters={store.adapters}
          adapter={store.adapter}
          onAdapterChange={(id) => store.setAdapter(id)}
          onStart={(url, task, adp) => void store.start(url, task, adp)}
          onExecute={(task) => void store.execute(task)}
          onExplore={(prompt) => void store.explorePrompt(prompt)}
          onStop={() => void store.stop()}
          onRefresh={() => void store.refresh()}
          onSubmitCreds={(u, p, extra) => void store.submitCredentials(u, p, extra)}
        />
      </div>

      <LookAtThatChat
        messages={store.chat}
        pending={store.chatPending}
        running={store.running}
        executing={store.executing}
        onSend={(m) => void store.sendChat(m)}
      />

      <LookAtThatLog
        logs={store.logs}
        onClear={() => useLookAtThatStore.setState({ logs: [] })}
      />

      {id && (
        <CalibrationCard
          runId={id}
          calibration={store.calibration}
          calibrating={store.calibrating}
          running={store.running}
          executing={store.executing}
          checklist={store.checklist}
          stuckStep={store.stuckStep}
          onCalibrate={() => void store.calibrate()}
          onRetryStep={() => void store.nudgeCalibrate()}
        />
      )}

      <TestQueuePanel
        tests={tests}
        running={store.running}
        playingKey={store.playingKey}
        queuedKeys={store.queuedKeys}
        playVerdicts={store.playVerdicts}
        onPlay={(keys) => void store.playTests(keys)}
        onStopQueue={() => void store.stopQueue()}
      />

      <GameMemoryCard notes={store.memory} onDelete={(nid) => void store.deleteMemoryNote(nid)} />

      {id && <ExploreGallery runId={id} items={store.explore} />}
    </div>
  );
}
